// Phone layout — pure geometry, shared by the page render (<Workspace>) and the
// drag engine (./engine.ts), so a drag's preview is exactly the layout that
// renders once it's dropped.
//
// The board is a list of ROWS. A row is one module (full width) or two "small"
// modules side by side (half width each). Small = "mobileSmall" in
// modules.json. A row is as tall as its taller module; rows stack with a gap.
// Heights are measured in half-widths (the width of a half-width module):
// one of H_LEVELS (1.25–3.75 in equal steps of 0.5).

export interface LayoutItem {
  id: string;
  small: boolean;
  /** Height in half-widths (one of H_LEVELS). */
  h: number;
  /** "Shares a row with the next module" (undefined = automatic pairing). */
  pairNext?: boolean;
}

export type Row = string[]; // 1 id = full width, 2 ids = a half-width pair

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Metrics {
  /** Left edge of a full-width row (px from the workspace's left edge). */
  left: number;
  full: number; // full-width module width
  half: number; // half-width module width (also the height unit)
  gap: number;
  padTop: number;
  padBottom: number;
}

export const GAP = 16;
/** The heights a module can be (half-widths): equal 0.5 steps. */
export const H_LEVELS = [1.25, 1.75, 2.25, 2.75, 3.25, 3.75];
export const H_MIN = H_LEVELS[0]!;
export const H_MAX = H_LEVELS[H_LEVELS.length - 1]!;

/** Full rows span the width less a GAP-wide margin each side (the same space
 *  as between modules); a pair = 2 halves + gap. The workspace's own side
 *  padding is ignored — modules are placed absolutely. */
export function metricsFor(
  width: number,
  pad: { left: number; right: number; top: number; bottom: number }
): Metrics {
  const full = Math.max(0, width - 2 * GAP);
  return {
    left: GAP,
    full,
    half: Math.max(40, (full - GAP) / 2),
    gap: GAP,
    padTop: pad.top,
    padBottom: pad.bottom,
  };
}

/** The nearest allowed height (a tie rounds down). */
export const clampH = (h: number) =>
  H_LEVELS.reduce((best, l) => (Math.abs(l - h) < Math.abs(best - h) ? l : best));

/** Rows from the module order. Pairing is explicit once anyone has arranged
 *  the board on a phone (some `pairNext` set); before that, neighbouring small
 *  modules pair up automatically. Only two small modules can pair. */
export function buildRows(items: LayoutItem[]): Row[] {
  const explicit = items.some((it) => it.pairNext !== undefined);
  const rows: Row[] = [];
  for (let i = 0; i < items.length; i++) {
    const a = items[i]!;
    const b = items[i + 1];
    if (b && a.small && b.small && (explicit ? a.pairNext === true : true)) {
      rows.push([a.id, b.id]);
      i++;
    } else {
      rows.push([a.id]);
    }
  }
  return rows;
}

/** The store arrangement for a set of rows: order + "pairs with next". */
export function rowsToArrangement(rows: Row[]): { id: string; pairNext: boolean }[] {
  return rows.flatMap((r) =>
    r.length === 2
      ? [{ id: r[0]!, pairNext: true }, { id: r[1]!, pairNext: false }]
      : [{ id: r[0]!, pairNext: false }]
  );
}

/** Where every module sits for a set of rows, plus the board's total height. */
export function layoutRows(
  rows: Row[],
  heightOf: (id: string) => number,
  m: Metrics
): { rects: Map<string, Rect>; height: number } {
  const rects = new Map<string, Rect>();
  let y = m.padTop;
  rows.forEach((r, i) => {
    if (i > 0) y += m.gap;
    if (r.length === 2) {
      const ha = heightOf(r[0]!) * m.half;
      const hb = heightOf(r[1]!) * m.half;
      rects.set(r[0]!, { x: m.left, y, w: m.half, h: ha });
      rects.set(r[1]!, { x: m.left + m.half + m.gap, y, w: m.half, h: hb });
      y += Math.max(ha, hb);
    } else {
      const h = heightOf(r[0]!) * m.half;
      rects.set(r[0]!, { x: m.left, y, w: m.full, h });
      y += h;
    }
  });
  return { rects, height: y + m.padBottom };
}

/**
 * Every place the dragged module could land, as the resulting rows. `rows` is
 * the board WITHOUT the dragged module (so a former pair partner is already
 * on its own row — "pulling a module out leaves both on their own rows").
 *  - Own row: between any two rows (or first / last).
 *  - Small modules only: beside a small module that's on its own (left or
 *    right) — this also covers swapping with its old partner; or into a pair,
 *    beside either half, which sends that half's partner to its own row
 *    (reading order kept).
 */
export function dropOptions(
  rows: Row[],
  dragged: string,
  draggedSmall: boolean,
  isSmall: (id: string) => boolean
): Row[][] {
  const out: Row[][] = [];
  for (let i = 0; i <= rows.length; i++) {
    out.push([...rows.slice(0, i), [dragged], ...rows.slice(i)]);
  }
  if (!draggedSmall) return out;
  rows.forEach((r, i) => {
    const before = rows.slice(0, i);
    const after = rows.slice(i + 1);
    if (r.length === 1 && isSmall(r[0]!)) {
      out.push([...before, [dragged, r[0]!], ...after]);
      out.push([...before, [r[0]!, dragged], ...after]);
    } else if (r.length === 2) {
      const [a, b] = r as [string, string];
      out.push([...before, [dragged, a], [b], ...after]);
      out.push([...before, [a, dragged], [b], ...after]);
      out.push([...before, [a], [dragged, b], ...after]);
      out.push([...before, [a], [b, dragged], ...after]);
    }
  });
  return out;
}

/** The board with two modules trading places. */
export const swapRows = (all: Row[], a: string, b: string): Row[] =>
  all.map((r) => r.map((x) => (x === a ? b : x === b ? a : x)));

/**
 * The swaps a drag can make (see aimRows): a small module can trade places
 * with either half of a pair, the other module taking its old spot.
 */
export function swapOptions(all: Row[], dragged: string, draggedSmall: boolean): Row[][] {
  if (!draggedSmall) return [];
  return all
    .filter((r) => r.length === 2 && !r.includes(dragged))
    .flat()
    .map((x) => swapRows(all, dragged, x));
}

export const sameRows = (a: Row[], b: Row[]) =>
  a.length === b.length && a.every((r, i) => r.join("|") === b[i]!.join("|"));

/**
 * Where a drag is aiming, as the resulting rows — decided against the board as
 * it was when the module was lifted (`all` + its `rects`), so targets never
 * move under the finger as the preview rearranges. (cx, cy) = the card's
 * centre. Over a module:
 *  - its top / bottom quarter → a row of its own, above / below that row;
 *  - otherwise, if both can share a row → beside it, on the half it's over;
 *    or, if it's half of a pair, swap with it (it takes the dragged module's
 *    old spot);
 *  - otherwise → above / below, by which half it's over.
 * Over the dragged module's own spot → where it started. Between rows → a row
 * of its own there.
 */
export function aimRows(
  all: Row[],
  rects: Map<string, Rect>,
  dragged: string,
  draggedSmall: boolean,
  isSmall: (id: string) => boolean,
  cx: number,
  cy: number
): Row[] {
  const without = all.map((r) => r.filter((x) => x !== dragged)).filter((r) => r.length > 0);
  const rowOf = (id: string) => without.findIndex((r) => r.includes(id));
  const ownRow = (k: number) => [...without.slice(0, k), [dragged], ...without.slice(k)];

  const own = rects.get(dragged);
  const inside = (r: Rect) => cx >= r.x && cx <= r.x + r.w && cy >= r.y && cy <= r.y + r.h;
  if (own && inside(own)) return all;

  let hit: string | null = null;
  rects.forEach((r, id) => {
    if (id !== dragged && inside(r)) hit = id;
  });

  if (hit === null) {
    // Between rows: a row of its own just above the first module below.
    let below: string | null = null;
    let belowY = Infinity;
    rects.forEach((r, id) => {
      if (id !== dragged && r.y > cy && r.y < belowY) {
        belowY = r.y;
        below = id;
      }
    });
    if (below === null) return ownRow(without.length);
    // The dragged module's old spot sits right above `below` → put it back.
    if (own && own.y < belowY && own.y + own.h >= cy - 1) return all;
    return ownRow(rowOf(below));
  }

  const x: string = hit;
  const r = rects.get(x)!;
  const k = rowOf(x);
  const fy = (cy - r.y) / r.h;
  const left = cx < r.x + r.w / 2;
  const before = without.slice(0, k);
  const after = without.slice(k + 1);
  const row = without[k]!;
  if (fy < 0.25) return ownRow(k);
  if (fy > 0.75) return ownRow(k + 1);
  if (draggedSmall && isSmall(x)) {
    // Half of a pair → swap with it (it takes the dragged module's old spot).
    if (row.length === 2) return swapRows(all, dragged, x);
    if (row.length === 1) return [...before, left ? [dragged, x] : [x, dragged], ...after];
  }
  return ownRow(fy < 0.5 ? k : k + 1);
}
